/**
 * Executive Summary (UAT §11).
 *
 * A leadership-facing view, never an independent source of truth — every number here is
 * read from the same engine as Department Status and Business Case. It exists to answer
 * "what needs a decision today" on one page.
 *
 * The Validation Gate runs through the whole view. This Week's Impact, Needs a Decision
 * and Monitoring all filter to validated departments together; everything Flagged or Watch
 * that hasn't passed the gate appears in its own Preliminary section rather than being
 * hidden. The gate controls what is presented as validated, not what is visible.
 *
 * Note this is NOT the "Executive Summary" report inside Reports, which deliberately
 * ignores the gate and counts every department. Same name, two views, and the difference
 * in their totals is real.
 */
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useModel, useDepartmentSignals } from '../state.jsx';

const money = (n) => '$' + Math.round(n).toLocaleString();

/** The one-line summary under a department name, in that department's own units. */
function detailFor(dep, signal) {
  if (signal.dataInvalid) return signal.invalidReasons.join('; ');
  if (signal.kind === 'volume') {
    return `Required ${signal.impliedFTE} FTE — Productive ${signal.productiveFTE} FTE — Gap ${signal.volGap} — Threshold ${dep.fteTrig ?? 0.5}`;
  }
  if (signal.kind === 'coverage') {
    return `${signal.covUncovered} uncovered block(s), ${signal.covSingle} single-point block(s)`;
  }
  if (signal.kind === 'support') {
    const lanes = signal.lanesOverThreshold?.length
      ? ` — over threshold: ${signal.lanesOverThreshold.join(', ')}`
      : '';
    return `Headcount-exposed ratio 1:${signal.hcRatioActual} vs 1:${signal.benchmark} benchmark${lanes}`;
  }
  return 'Manager escalation — no formula behind this department by design.';
}

function Row({ dep, signal, tag, tagColor, tagBg, right, sub }) {
  return (
    <div className="exec-row">
      <span className="exec-tag" style={{ color: tagColor, background: tagBg }}>{tag}</span>
      <div className="exec-row-body">
        <div className="exec-row-name">{dep.name}</div>
        <div className="exec-row-detail">{detailFor(dep, signal)}</div>
      </div>
      {right && (
        <div className="exec-row-right">
          <div className="exec-row-right-value num">{right}</div>
          {sub && <div className="exec-row-right-sub">{sub}</div>}
        </div>
      )}
    </div>
  );
}

function Section({ title, count, children, empty }) {
  return (
    <section className="exec-section">
      <h2>{title}{count != null && ` (${count})`}</h2>
      {children || <p className="exec-empty">{empty}</p>}
    </section>
  );
}

export function ExecutiveSummary() {
  const { state, engine } = useModel();
  const signals = useDepartmentSignals();

  const view = useMemo(() => {
    if (!state || !engine) return null;

    const impact = engine.computeWeeklyImpact();
    const validated = signals.filter(({ dep }) => engine.isApprovedForReporting(dep));

    // Data Invalid first and loudest, then formula-driven Flagged worst-first, then
    // judgment departments carrying a manager escalation — labelled as such, so nothing
    // judgment-based is ever disguised as formula-based.
    const invalid = validated.filter(({ signal }) => signal.dataInvalid);
    const flagged = validated
      .filter(({ signal }) => signal.flagged && signal.kind !== 'judgment')
      .sort((a, b) => (b.severity ?? 0) - (a.severity ?? 0));
    const judgmentFlagged = validated.filter(({ signal }) => signal.flagged && signal.kind === 'judgment');
    const watching = validated.filter(({ signal }) => !signal.flagged && signal.watch);

    // Real formula output that simply hasn't been signed off yet — walled off, not hidden.
    const preliminary = signals.filter(
      ({ dep, signal }) => !engine.isApprovedForReporting(dep) && (signal.flagged || signal.watch),
    );

    // Governance is tracked independently of status: a department can be Healthy today and
    // still be running on a methodology nobody has approved.
    const governance = signals.filter(
      ({ dep, signal }) =>
        signal.kind === 'volume' &&
        ((dep.volDrivers?.length > 1 && !dep.volCombineLocked) ||
          ((dep.volPeriodBasis ?? 'current') !== 'current' && !dep.volPeriodBasisLocked)),
    );

    // Judgment departments are never folded into "Healthy" — counting work with no formula
    // behind it as formula-verified would overstate how much has actually been validated.
    const breakdown = {
      healthy: signals.filter(({ signal }) => !signal.notConfigured && !signal.dataInvalid && !signal.flagged && !signal.watch && signal.kind !== 'judgment').length,
      watch: signals.filter(({ signal }) => !signal.flagged && signal.watch && signal.kind !== 'judgment').length,
      flagged: signals.filter(({ signal }) => signal.flagged && signal.kind !== 'judgment').length,
      judgment: signals.filter(({ signal }) => signal.kind === 'judgment' && !signal.flagged && !signal.watch).length,
      notConfigured: signals.filter(({ signal }) => signal.notConfigured).length,
      dataInvalid: signals.filter(({ signal }) => signal.dataInvalid).length,
    };

    const headcount = state.team.filter((e) => engine.isActiveRosterRecord(e) && !e.vacant).length;
    const openPositions = state.team.filter((e) => engine.isVacancyRecruitable(e)).length;

    return { impact, invalid, flagged, judgmentFlagged, watching, preliminary, governance, breakdown, headcount, openPositions };
  }, [state, engine, signals]);

  if (!view) return null;
  const { impact } = view;

  const decisions = [...view.invalid, ...view.flagged, ...view.judgmentFlagged];

  return (
    <div className="page">
      {impact.unapprovedCount > 0 && (
        <div className="draft-pilot-banner">
          ⚠ DRAFT PILOT — {impact.totalDeptCount - impact.unapprovedCount} of {impact.totalDeptCount}{' '}
          departments have passed the Validation Gate. The remaining {impact.unapprovedCount} are
          preliminary and excluded from Executive Summary totals until each department's owner
          validates its methodology.
        </div>
      )}

      <div className="page-hdr">
        <div className="page-hdr-title">Executive Summary</div>
        <div className="page-hdr-sub">
          What needs a decision today — everything else is one line. Never a source of truth on
          its own; every number here also lives on Department Status or Business Case.
        </div>
      </div>

      <div className="exec-hero">
        <div className="exec-hero-label">This Week’s Impact</div>
        <div className="exec-hero-line">
          <span className="exec-hero-value num">{impact.hrsShort}</span>
          <span className="exec-hero-unit">
            hrs/week short across {impact.flaggedCount} Flagged department
            {impact.flaggedCount !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="exec-hero-trend">
          {impact.trend
            ? impact.trend.direction === 'steady'
              ? '→ steady vs. 7 days ago'
              : impact.trend.direction === 'worsening'
                ? `↑ ${Math.abs(impact.trend.delta)} hrs/wk worse than 7 days ago`
                : `↓ ${Math.abs(impact.trend.delta)} hrs/wk better than 7 days ago`
            : 'insufficient history for a trend yet'}
        </div>
        {impact.unapprovedCount > 0 && (
          <div className="exec-hero-scope">
            Scope: {impact.totalDeptCount - impact.unapprovedCount} of {impact.totalDeptCount}{' '}
            departments have passed the Validation Gate
            {impact.unapprovedFlaggedCount > 0 && (
              <> — {impact.unapprovedFlaggedCount} more department
                {impact.unapprovedFlaggedCount !== 1 ? 's show' : ' shows'} Flagged but{' '}
                {impact.unapprovedFlaggedCount !== 1 ? 'aren’t' : 'isn’t'} counted here yet
                (methodology not yet validated)</>
            )}
            {' — '}
            <Link to="/configure/depts">manage in Configure</Link>
          </div>
        )}
      </div>

      <div className="exec-kpis">
        <div className="kpi">
          <div className="kpi-label">Loaded Filled Headcount</div>
          <div className="kpi-value">{view.headcount}</div>
          <div className="kpi-sub">active employees currently loaded</div>
        </div>
        <div className="kpi amber">
          <div className="kpi-label">Open Positions</div>
          <div className="kpi-value">{view.openPositions}</div>
          <div className="kpi-sub">approved, funded, and recruitable now</div>
        </div>
      </div>

      <Section title="Needs a Decision" empty="Nothing validated needs a decision today.">
        {decisions.length > 0 && (
          <div className="exec-rows">
            {decisions.map(({ dep, signal }) => {
              const revenue = engine.computeRevenueAtRisk(dep.id, signal.flagged, signal.watch);
              return (
                <Row
                  key={dep.id}
                  dep={dep}
                  signal={signal}
                  tag={signal.dataInvalid ? 'DATA INVALID' : signal.kind === 'judgment' ? 'JUDGMENT — FLAGGED' : 'FORMULA — FLAGGED'}
                  tagColor={signal.dataInvalid ? '#5B21B6' : signal.kind === 'judgment' ? 'var(--blue)' : 'var(--red)'}
                  tagBg={signal.dataInvalid ? '#F5F3FF' : signal.kind === 'judgment' ? 'var(--ltblue)' : 'var(--ltred)'}
                  right={revenue > 0 ? money(revenue) : 'N/A — internal work'}
                  sub={revenue > 0 ? 'estimated revenue at risk' : null}
                />
              );
            })}
          </div>
        )}
      </Section>

      <Section
        title="Monitoring — Watch"
        count={view.watching.length}
        empty="No departments currently in Watch."
      >
        {view.watching.length > 0 && (
          <div className="exec-rows">
            {view.watching.map(({ dep, signal }) => (
              <Row key={dep.id} dep={dep} signal={signal} tag="WATCH" tagColor="var(--amber)" tagBg="var(--ltamber)" />
            ))}
          </div>
        )}
      </Section>

      {view.preliminary.length > 0 && (
        <Section title="Preliminary — Not Yet Validated for Reporting" count={view.preliminary.length}>
          <p className="exec-note">
            These departments haven’t passed the Validation Gate yet (Configure → Departments).
            Their results are real formula output, not fabricated — they simply aren’t counted
            as validated organizational findings yet.
          </p>
          <div className="exec-rows">
            {view.preliminary.map(({ dep, signal }) => (
              <Row
                key={dep.id}
                dep={dep}
                signal={signal}
                tag={signal.flagged ? 'FLAGGED' : 'WATCH'}
                tagColor={signal.flagged ? 'var(--red)' : 'var(--amber)'}
                tagBg={signal.flagged ? 'var(--ltred)' : 'var(--ltamber)'}
                right={signal.kind === 'judgment' ? 'judgment' : 'formula'}
              />
            ))}
          </div>
        </Section>
      )}

      {view.governance.length > 0 && (
        <Section title="Governance — Methodology Pending Approval" count={view.governance.length}>
          <div className="exec-rows">
            {view.governance.map(({ dep, signal }) => (
              <Row key={dep.id} dep={dep} signal={signal} tag="UNLOCKED" tagColor="var(--ink3)" tagBg="var(--grey2)" />
            ))}
          </div>
        </Section>
      )}

      {/* Zero counts are omitted, as in the legacy build — a category with nothing in it
          is noise, not information. */}
      <div className="exec-breakdown">
        {[
          [view.breakdown.healthy, 'Formula-based Healthy'],
          [view.breakdown.watch, 'Formula-based Watch'],
          [view.breakdown.flagged, 'Formula-based Flagged'],
          [view.breakdown.judgment, 'Informational/Judgment-based (no active escalation)'],
          [view.breakdown.notConfigured, 'Configuration Required'],
          [view.breakdown.dataInvalid, 'Data Invalid'],
        ]
          .filter(([count]) => count > 0)
          .map(([count, label]) => `${count} ${label}`)
          .join(' · ')}
      </div>
    </div>
  );
}
